import { faMessage, faPhone } from "@fortawesome/pro-regular-svg-icons";

import Image from "common/components/Image";
import Icon from "common/components/Icon";
import { toPhone } from "common/utils/formatting";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { useClinic } from "client/clinics/hooks";
import Alert from "client/common/components/content/Alert";

import dogDoctor from "../images/dog_doctor.svg";
import { AppointmentBookingAlertDrawerProps } from "../types/stackables";

import styles from "../styles/AppointmentBookingAlertDrawer.module.less";

const AppointmentBookingAlertDrawer = ({
  isEmergency,
  appointmentKind,
  blockedPatient,
}: AppointmentBookingAlertDrawerProps) => {
  const { close } = useCurrentDrawer();
  const clinic = useClinic();

  const textNumber = clinic.communication_number?.number;
  const callNumber = clinic.phone_number;
  const textNumberDisplay = textNumber ? toPhone(textNumber) : null;
  const callNumberDisplay = callNumber ? toPhone(callNumber) : null;

  const renderDescription = () => {
    if (textNumberDisplay && callNumberDisplay && textNumberDisplay === callNumberDisplay) {
      return (
        <>
          please give us a call at our office or message us via SMS{" "}
          <a href={`tel:${callNumber}`} className={styles.phone}>
            {callNumberDisplay}
          </a>
        </>
      );
    }

    if (callNumberDisplay && textNumberDisplay) {
      return (
        <>
          please give us a call at our office{" "}
          <a href={`tel:${callNumber}`} className={styles.phone}>
            {callNumberDisplay}
          </a>{" "}
          or message us via SMS{" "}
          <a href={`sms:${textNumber}`} className={styles.phone}>
            {textNumberDisplay}
          </a>
        </>
      );
    }

    if (textNumberDisplay) {
      return (
        <>
          please message us via SMS{" "}
          <a href={`sms:${textNumber}`} className={styles.phone}>
            {textNumberDisplay}
          </a>
        </>
      );
    }

    if (callNumberDisplay) {
      return (
        <>
          please give us a call at our office{" "}
          <a href={`tel:${callNumber}`} className={styles.phone}>
            {callNumberDisplay}
          </a>
        </>
      );
    }

    return "please give us a call at our office";
  };

  const renderErrorDescription = () => {
    if (blockedPatient) {
      // If no appointment kind is provided, it means no providers see this species
      if (!appointmentKind) {
        return (
          <div className={styles.description}>
            {blockedPatient.name_display}&apos;s species is{" "}
            <strong>not available for online booking</strong>.
          </div>
        );
      }
      return (
        <div className={styles.description}>
          This appointment type is{" "}
          <strong>not available for {blockedPatient.name_display}&apos;s species.</strong>
        </div>
      );
    }
    // No appointment kind generally means turbo classified a non-enabled kind
    if (!appointmentKind || !appointmentKind.is_client_bookable) {
      return (
        <div className={styles.description}>
          This appointment type is <strong>not available for online booking</strong>.
        </div>
      );
    }
    return null;
  };

  return (
    <DrawerLayout>
      <DrawerLayout.Header onClose={close} />
      <DrawerLayout.Content>
        <div className={styles.content}>
          <Image src={dogDoctor} alt="Dog doctor" className={styles.image} />
          <div className={styles.title}>
            <span className={styles.ruh}>Ruh-Oh!</span> Please give us a call
          </div>
          {renderErrorDescription()}
          <div className={styles.description}>
            In order to complete booking, {renderDescription()} to schedule an appointment.
          </div>
          <div className={styles.availability}>
            Availability this week
            <div className={styles.openUntil}>{clinic.daily_hours}</div>
          </div>
        </div>
        {isEmergency && (
          <Alert variant="danger">
            Urgent Need: Please call the hospital if they can see your pet today. *Surgeries require
            consultation
          </Alert>
        )}
        <DrawerLayout.Actions>
          {callNumber && (
            <DrawerLayout.Actions.Button onClick={() => window.open(`tel:${callNumber}`)}>
              <Icon icon={faPhone} />
              Call clinic
            </DrawerLayout.Actions.Button>
          )}
          {textNumber && (
            <DrawerLayout.Actions.Button onClick={() => window.open(`sms:${textNumber}`)}>
              <Icon icon={faMessage} />
              Text clinic
            </DrawerLayout.Actions.Button>
          )}
        </DrawerLayout.Actions>
      </DrawerLayout.Content>
    </DrawerLayout>
  );
};

export default AppointmentBookingAlertDrawer;
